/// <amd-module name="Pipeline/Analytics/PipelineAnalyticsHeader/Pipeline.AnalyticsHeader" />
import { AnalyticsHeaderComponent } from "Analytics/Shared/Common/AnalyticsHeaderComponent";
import { PeriodConfigurationProps } from "Analytics/Shared/Common/Definitions";
import { ContextType, IReportContext, Period, TrendBy } from "Analytics/Shared/Common/Types";
import { ITrendChartReportConfiguration } from "Analytics/Shared/TrendChart/Common/Types";
import { IFilterItem } from "Analytics/Shared/TrendChart/Common/Types";
import * as Resources from "Pipeline/Analytics/PipelineAnalyticsHeader/Resources";
import { BranchSource } from "Pipeline/Analytics/PipelineAnalyticsHeader/Sources/BranchSource";
import * as React from "react";
import { localeFormat } from "VSS/Core/Util/String";
import { Components, IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { PickListFilterBarItem } from "VSSUI/Components/PickList/PickListFilterBarItem";
import { FilterBar } from "VSSUI/FilterBar";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Filter, FILTER_CHANGE_EVENT, IFilter as IVSSUIFilter, IFilterState as IVSSUIFilterState } from "VSSUI/Utilities/Filter";

export interface IPipelineAnalyticsHeaderProps extends IVssComponentProperties {
    headerTitle: string;
    definitionName: string;
    definitionId: number;
    hideFilters?: boolean;
    maxPeriod?: Period;
    onPeriodChanged?: (confValues: ITrendChartReportConfiguration) => void;
    onBranchChanged?: (confValues: ITrendChartReportConfiguration) => void;
}

export interface IAnalyticsPageState {
    selectedPeriod: Period;
    branches: IFilterItem[];
    selectedBranches: IFilterItem[];
    filter: Filter;
    showFilters: boolean;
}

export class PipelineAnalyticsHeader extends VssComponent<IPipelineAnalyticsHeaderProps, IAnalyticsPageState> {
    private _reportContext: IReportContext;
    private _periodOptions = new PeriodConfigurationProps(Period.Days_180).options;
    private _branchFilterKey = "branch";
    private _maxPeriod: Period;

    constructor(props: IPipelineAnalyticsHeaderProps, context: IVssComponentContext) {
        super(props);

        this._maxPeriod = this.props.maxPeriod || Period.Days_30;
        this.state = { selectedPeriod: Period.Days_14, filter: new Filter() } as IAnalyticsPageState;

        this._reportContext = {
            contextType: ContextType.Build,
            definitionId: props.definitionId
        } as IReportContext;
    }

    public componentDidMount() {
        const { filter } = this.state;
        if (filter) {
            this._subscribeToFilterChangeEvent(filter);
        }
    }

    public componentWillUnmount() {
        const { filter } = this.state;
        if (filter) {
            this._unsubscribeFromFilterChangeEvent(filter);
        }
    }

    public render() {
        return (
            <>
                <AnalyticsHeaderComponent title={this.props.headerTitle} showCommandBar={true} commandBarItems={this._getCommandBarItems()}>
                    {this._renderSubHeader(this.props.definitionName)}
                </AnalyticsHeaderComponent>
                {this.state.showFilters && (
                    <div className="pipeline-analytics-filter-bar-container page-content page-content-top">
                        <FilterBar filter={this.state.filter}>
                            <PickListFilterBarItem
                                key={this._branchFilterKey}
                                filter={this.state.filter}
                                filterItemKey={this._branchFilterKey}
                                selectionMode={2}
                                selectedItems={this.state.selectedBranches}
                                getPickListItems={this._getBranchItems}
                                getListItem={(item: IFilterItem) => {
                                    return { name: item.name, key: item.id.toString() };
                                }}
                                placeholder={Resources.BranchNoPlaceHeader}
                                noItemsText={Resources.BranchNoItemsText}
                                showSelectAll={false}
                                isSearchable={true}
                                searchTextPlaceholder={Resources.Search}
                                disabled={false}
                                hideSelectedItemIcon={true}
                            />
                        </FilterBar>
                    </div>
                )}
            </>
        );
    }

    private async _getBranchInfo(context: IVssComponentContext): Promise<void> {
        const source = new BranchSource(context.pageContext);
        const branches = await source.queryBranches(this._reportContext, Period.Days_30);
        this.setState({ branches: branches });
    }

    private _getCommandBarItems(): IHeaderCommandBarItem[] {
        if (this.props.hideFilters) {
            return [];
        }

        const showingFilters = this.state.showFilters;
        return [
            {
                id: "periodSelector",
                text: this._periodOptions[this.state.selectedPeriod],
                iconProps: {
                    iconName: "Calendar"
                },
                className: "period-selector",
                important: true,
                subtle: true,
                ariaLabel: localeFormat(Resources.PeriodSelectedAriaLabel, this._periodOptions[this.state.selectedPeriod]),
                subMenuProps: {
                    id: "periodSubMenu",
                    items: this._getPeriodCommandItems()
                }
            },
            {
                id: "filter-toggle",
                iconProps: {
                    iconName: showingFilters ? "FilterSolid" : "Filter"
                },
                ariaLabel: showingFilters ? Resources.HideFilters : Resources.ShowFilters,
                tooltipProps: {
                    text: showingFilters ? Resources.HideFilters : Resources.ShowFilters
                },
                className: "filter-icon",
                subtle: true,
                onActivate: () => this._onFilterChanged()
            }
        ];
    }

    private _getBranchItems = (): Promise<IFilterItem[]> => {
        const source = new BranchSource(this.context.pageContext);
        return source.queryBranches(this._reportContext, Period.Days_30);
    };

    private _onFilterChanged = (): void => {
        this.setState({ showFilters: !this.state.showFilters });
    };

    private _getPeriodCommandItems(): IHeaderCommandBarItem[] {
        const commandItems = [
            {
                id: "7days",
                text: this._periodOptions[Period.Days_7],
                onActivate: () => this._onPeriodChanged(Period.Days_7)
            }
        ];

        if (this._maxPeriod > Period.Days_7) {
            commandItems.push({
                id: "14days",
                text: this._periodOptions[Period.Days_14],
                onActivate: () => this._onPeriodChanged(Period.Days_14)
            });
        }

        if (this._maxPeriod > Period.Days_14) {
            commandItems.push({
                id: "30days",
                text: this._periodOptions[Period.Days_30],
                onActivate: () => this._onPeriodChanged(Period.Days_30)
            });
        }

        if (this._maxPeriod > Period.Days_30) {
            commandItems.push({
                id: "180days",
                text: this._periodOptions[Period.Days_180],
                onActivate: () => this._onPeriodChanged(Period.Days_180)
            });
        }

        return commandItems;
    }

    private _renderSubHeader(definitionName: string): JSX.Element {
        return <div className="pipeline-analytics-sub-header fontSizeM fontWeightLighter">{definitionName || ""}</div>;
    }

    private _onPeriodChanged = (period: Period) => {
        const confValues = {
            period: period,
            trendBy: TrendBy.Days,
            branchItems: this.state.selectedBranches
        } as ITrendChartReportConfiguration;

        this.setState({ selectedPeriod: period });
        this.props.onPeriodChanged!(confValues);
    };

    private _subscribeToFilterChangeEvent(filter: IVSSUIFilter) {
        filter.subscribe(this._handleFilterUpdated, FILTER_CHANGE_EVENT);
    }

    private _unsubscribeFromFilterChangeEvent(filter: IVSSUIFilter) {
        filter.unsubscribe(this._handleFilterUpdated, FILTER_CHANGE_EVENT);
    }

    private _handleFilterUpdated = (changedState: IVSSUIFilterState): void => {
        const branchItems = changedState[this._branchFilterKey] ? (changedState[this._branchFilterKey]!.value as IFilterItem[]) : [];
        const confValues = {
            period: this.state.selectedPeriod,
            trendBy: TrendBy.Days,
            branchItems: branchItems
        } as ITrendChartReportConfiguration;

        this.setState({ selectedBranches: branchItems }, () => {
            this.props.onBranchChanged!(confValues);
        });
    };
}

Components.add("pipelineAnalyticsHeader", PipelineAnalyticsHeader);
